/**
 * Local address book. Labels only — stored unencrypted on this device because
 * it holds no key material, only public destinations the user chose to save.
 */

const CONTACTS_KEY = "vaultkey.contacts.v1";

export interface Contact {
  id: string;
  label: string;
  address: string;
  /** XRPL destination tag, required by most exchanges */
  tag?: string;
  note?: string;
  createdAt: number;
}

export function readContacts(): Contact[] {
  try {
    const raw = localStorage.getItem(CONTACTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Contact[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeContacts(next: Contact[]) {
  localStorage.setItem(CONTACTS_KEY, JSON.stringify(next));
}

export function sanitizeLabel(label: string): string {
  const trimmed = label.trim().replace(/\s+/gu, " ");
  if (!trimmed) throw new Error("Give the recipient a label.");
  if (trimmed.length > 40) throw new Error("Labels must be 40 characters or fewer.");
  return trimmed;
}

export function sanitizeTag(tag: string): string | undefined {
  const t = tag.trim();
  if (!t) return undefined;
  if (!/^\d{1,10}$/u.test(t) || Number(t) > 4_294_967_295) {
    throw new Error("Destination tag must be a whole number.");
  }
  return t;
}

export function sanitizeNote(note: string): string | undefined {
  const n = note.trim();
  if (!n) return undefined;
  if (n.length > 140) throw new Error("Notes must be 140 characters or fewer.");
  return n;
}

export function newContactId(): string {
  return crypto.randomUUID();
}
